import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Divider,
  alpha,
  Container,
  Breadcrumbs,
  Link as MuiLink,
  Pagination,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  PersonAdd as PersonAddIcon,
  School as SchoolIcon,
  Cancel as CancelIcon,
  LocalOffer as LocalOfferIcon,
  DirectionsCar as DirectionsCarIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  MarkEmailRead as MarkReadIcon,
  Visibility as ViewIcon,
  Home as HomeIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Sort as SortIcon
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationsContext';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const {
    notifications,
    loading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    getNotificationConfig,
    loadNotifications
  } = useNotifications();

  // SafeLine color scheme
  const safeLineColors = {
    flameOrange: '#ff6b35',
    brightOrange: '#ff9500',
    deepOrange: '#e55100',
    orangeTint: '#fff4f0',
    pureWhite: '#ffffff',
    lightGray: '#f8f9fa',
    coolGray: '#e9ecef',
    mediumGray: '#dee2e6',
    emerald: '#10b981',
    lightEmerald: '#d1fae5',
    forestGreen: '#047857',
    amber: '#f59e0b',
    lightAmber: '#fef3c7',
    darkAmber: '#d97706',
    red: '#ef4444',
    lightRed: '#fee2e2',
    darkRed: '#dc2626',
    darkCharcoal: '#1f2937',
    mediumCharcoal: '#374151',
    lightCharcoal: '#6b7280',
    mutedGray: '#9ca3af'
  };

  // State for filtering and sorting
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);

  const itemsPerPage = 10;

  // Filter and sort notifications
  const filteredNotifications = notifications
    .filter(notification => {
      // Search filter
      if (searchQuery && !notification.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !notification.message.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      
      // Type filter
      if (typeFilter !== 'all' && notification.type !== typeFilter) {
        return false;
      }
      
      // Status filter (read/unread)
      if (statusFilter === 'read' && !notification.read) return false;
      if (statusFilter === 'unread' && notification.read) return false;
      
      // Unread only toggle
      if (showUnreadOnly && notification.read) return false;
      
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.timestamp) - new Date(a.timestamp);
        case 'oldest':
          return new Date(a.timestamp) - new Date(b.timestamp);
        case 'unread':
          if (a.read === b.read) return new Date(b.timestamp) - new Date(a.timestamp);
          return a.read ? 1 : -1;
        default:
          return 0;
      }
    });

  // Pagination
  const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedNotifications = filteredNotifications.slice(startIndex, startIndex + itemsPerPage);

  // Notification type options for filter
  const notificationTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'GUEST_ARRIVAL', label: 'Guest Arrivals' },
    { value: 'GUARDIAN_VERIFICATION', label: 'Guardian Verification' },
    { value: 'PICKUP_COMPLETED', label: 'Pickup Completed' },
    { value: 'SYSTEM_ALERT', label: 'System Alerts' },
    { value: 'TAG_ASSIGNMENT', label: 'Tag Assignments' },
    { value: 'GUARDIAN_APPROVED', label: 'Guardian Approved' },
    { value: 'GUARDIAN_DENIED', label: 'Guardian Denied' }
  ];

  const getNotificationIcon = (type) => {
    const iconMap = {
      'person': <PersonIcon />,
      'security': <SecurityIcon />,
      'check_circle': <CheckCircleIcon />,
      'warning': <WarningIcon />,
      'person_add': <PersonAddIcon />,
      'school': <SchoolIcon />,
      'cancel': <CancelIcon />,
      'local_offer': <LocalOfferIcon />,
      'directions_car': <DirectionsCarIcon />,
      'info': <InfoIcon />
    };

    const config = getNotificationConfig(type);
    return iconMap[config.icon] || <InfoIcon />;
  };

  const getNotificationColor = (type) => {
    const config = getNotificationConfig(type);
    const colorMap = {
      'primary': safeLineColors.flameOrange,
      'warning': safeLineColors.amber,
      'success': safeLineColors.emerald,
      'error': safeLineColors.red,
      'info': safeLineColors.mediumCharcoal
    };
    return colorMap[config.color] || safeLineColors.mediumCharcoal;
  };

  const getNotificationBgColor = (type) => {
    const config = getNotificationConfig(type);
    const colorMap = {
      'primary': safeLineColors.orangeTint,
      'warning': safeLineColors.lightAmber,
      'success': safeLineColors.lightEmerald,
      'error': safeLineColors.lightRed,
      'info': safeLineColors.lightGray
    };
    return colorMap[config.color] || safeLineColors.lightGray;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return `Today at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    } else if (days === 1) {
      return `Yesterday at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
  };

  const handleNotificationClick = (notification) => {
    const config = getNotificationConfig(notification.type);
    
    // Mark as read if not already read
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // Navigate to the relevant page
    navigate(config.navigateTo);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadNotifications();
    setTimeout(() => setRefreshing(false), 1000);
  };

  const handleMarkAsRead = (notificationId, event) => {
    event.stopPropagation();
    markAsRead(notificationId);
  };

  const handleDeleteNotification = (notificationId, event) => {
    event.stopPropagation();
    deleteNotification(notificationId);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setTypeFilter('all');
    setStatusFilter('all');
    setSortBy('newest');
    setShowUnreadOnly(false);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 400,
        flexDirection: 'column',
        gap: 2
      }}>
        <Typography variant="h6" sx={{ color: safeLineColors.mediumCharcoal }}>
          Loading notifications...
        </Typography>
        <Box sx={{ 
          width: 40,
          height: 40,
          border: `3px solid ${safeLineColors.coolGray}`,
          borderTop: `3px solid ${safeLineColors.flameOrange}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          '@keyframes spin': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' }
          }
        }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      backgroundColor: safeLineColors.lightGray, 
      minHeight: '100vh',
      pb: 4
    }}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs 
          aria-label="breadcrumb" 
          sx={{ 
            mb: 3,
            '& .MuiBreadcrumbs-separator': {
              color: safeLineColors.mutedGray
            }
          }}
        >
          <MuiLink 
            component={RouterLink}
            to="/dashboard"
            underline="hover" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              color: safeLineColors.mediumCharcoal,
              cursor: 'pointer',
              '&:hover': {
                color: safeLineColors.flameOrange
              }
            }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />
            Dashboard
          </MuiLink>
          <Typography 
            color="text.primary" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              color: safeLineColors.flameOrange,
              fontWeight: 'medium'
            }}
          >
            <NotificationsIcon sx={{ mr: 0.5, fontSize: 16 }} />
            Notifications
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${safeLineColors.pureWhite} 0%, ${safeLineColors.orangeTint} 100%)`,
            border: `1px solid ${safeLineColors.coolGray}`,
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
            }
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: 3
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <Box sx={{ 
                  p: 2, 
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`,
                  mr: 3,
                  boxShadow: `0 8px 16px ${alpha(safeLineColors.flameOrange, 0.3)}`
                }}>
                  <NotificationsIcon sx={{ color: safeLineColors.pureWhite, fontSize: 32 }} />
                </Box>
                <Box>
                  <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{ 
                      fontWeight: 700,
                      color: safeLineColors.darkCharcoal,
                      mb: 1
                    }}
                  >
                    Notifications Center
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: safeLineColors.mediumCharcoal,
                      fontWeight: 400,
                      mb: 2
                    }}
                  >
                    Stay updated with real-time school notifications
                  </Typography>
                  
                  {/* Stats */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip 
                      label={`${notifications.length} Total`}
                      sx={{
                        backgroundColor: safeLineColors.lightGray,
                        color: safeLineColors.darkCharcoal,
                        fontWeight: 'medium'
                      }}
                    />
                    {unreadCount > 0 && (
                      <Chip 
                        label={`${unreadCount} Unread`}
                        sx={{
                          backgroundColor: safeLineColors.flameOrange,
                          color: safeLineColors.pureWhite,
                          fontWeight: 'medium'
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={refreshing}
                  sx={{
                    borderColor: safeLineColors.flameOrange,
                    color: safeLineColors.flameOrange,
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    fontWeight: 'medium',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: safeLineColors.orangeTint,
                      borderColor: safeLineColors.flameOrange
                    }
                  }}
                >
                  {refreshing ? 'Refreshing...' : 'Refresh'}
                </Button>
                {unreadCount > 0 && (
                  <Button
                    variant="contained"
                    startIcon={<MarkReadIcon />}
                    onClick={markAllAsRead}
                    sx={{
                      background: `linear-gradient(135deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`,
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      fontWeight: 'medium',
                      textTransform: 'none',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${safeLineColors.brightOrange}, ${safeLineColors.flameOrange})`
                      }
                    }}
                  >
                    Mark All Read
                  </Button>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Filters and Search */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            backgroundColor: safeLineColors.pureWhite,
            border: `1px solid ${safeLineColors.coolGray}`
          }}
        >
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FilterIcon sx={{ mr: 1, color: safeLineColors.flameOrange }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: safeLineColors.darkCharcoal }}>
                  Filter & Search
                </Typography>
              </Box>
            }
            sx={{ 
              backgroundColor: safeLineColors.orangeTint,
              borderBottom: `1px solid ${safeLineColors.coolGray}`,
              pb: 2,
              pt: 2
            }}
          />
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search notifications..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: safeLineColors.flameOrange }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: safeLineColors.coolGray
                      },
                      '&:hover fieldset': {
                        borderColor: safeLineColors.flameOrange
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: safeLineColors.flameOrange
                      }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={typeFilter}
                    label="Type"
                    onChange={(e) => setTypeFilter(e.target.value)}
                    sx={{
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: safeLineColors.coolGray
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: safeLineColors.flameOrange
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: safeLineColors.flameOrange
                      }
                    }}
                  >
                    {notificationTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: safeLineColors.coolGray
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: safeLineColors.flameOrange
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: safeLineColors.flameOrange
                      }
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="unread">Unread</MenuItem>
                    <MenuItem value="read">Read</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={sortBy}
                    label="Sort By"
                    onChange={(e) => setSortBy(e.target.value)}
                    sx={{
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: safeLineColors.coolGray
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: safeLineColors.flameOrange
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: safeLineColors.flameOrange
                      }
                    }}
                  >
                    <MenuItem value="newest">Newest First</MenuItem>
                    <MenuItem value="oldest">Oldest First</MenuItem>
                    <MenuItem value="unread">Unread First</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={2}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showUnreadOnly}
                        onChange={(e) => setShowUnreadOnly(e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: safeLineColors.flameOrange,
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: safeLineColors.flameOrange,
                          },
                        }}
                      />
                    }
                    label="Unread Only"
                    sx={{ 
                      color: safeLineColors.darkCharcoal,
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.9rem'
                      }
                    }}
                  />
                  <Button
                    size="small"
                    onClick={clearFilters}
                    sx={{
                      color: safeLineColors.mediumCharcoal,
                      textTransform: 'none',
                      fontSize: '0.8rem'
                    }}
                  >
                    Clear Filters
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Notifications List */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: safeLineColors.pureWhite,
            border: `1px solid ${safeLineColors.coolGray}`,
            overflow: 'hidden'
          }}
        >
          <CardHeader
            title={
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: safeLineColors.darkCharcoal }}>
                  {filteredNotifications.length} Notifications
                </Typography>
                <Typography variant="body2" sx={{ color: safeLineColors.mediumCharcoal }}>
                  Page {currentPage} of {totalPages}
                </Typography>
              </Box>
            }
            sx={{ 
              backgroundColor: safeLineColors.orangeTint,
              borderBottom: `1px solid ${safeLineColors.coolGray}`,
              pb: 2,
              pt: 2
            }}
          />
          
          {paginatedNotifications.length > 0 ? (
            <>
              <List sx={{ p: 0 }}>
                {paginatedNotifications.map((notification) => (
                  <ListItem
                    key={notification.id}
                    button
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      py: 3,
                      px: 3,
                      borderBottom: `1px solid ${safeLineColors.coolGray}`,
                      backgroundColor: notification.read ? 'transparent' : alpha(safeLineColors.orangeTint, 0.3),
                      '&:hover': {
                        backgroundColor: alpha(safeLineColors.orangeTint, 0.5)
                      },
                      '&:last-child': {
                        borderBottom: 'none'
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 56 }}>
                      <Avatar sx={{
                        backgroundColor: getNotificationBgColor(notification.type),
                        color: getNotificationColor(notification.type),
                        width: 48,
                        height: 48
                      }}>
                        {getNotificationIcon(notification.type)}
                      </Avatar>
                    </ListItemIcon>
                    
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: notification.read ? 'medium' : 'bold',
                              color: safeLineColors.darkCharcoal,
                              flex: 1
                            }}
                          >
                            {notification.title}
                          </Typography>
                          {!notification.read && (
                            <Box sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              backgroundColor: safeLineColors.flameOrange
                            }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              color: safeLineColors.mediumCharcoal,
                              mb: 1,
                              lineHeight: 1.5
                            }}
                          >
                            {notification.message}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: safeLineColors.mutedGray,
                              fontWeight: 'medium'
                            }}
                          >
                            {formatTimestamp(notification.timestamp)}
                          </Typography>
                        </Box>
                      }
                    />
                    
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {!notification.read && (
                          <Tooltip title="Mark as read">
                            <IconButton 
                              onClick={(e) => handleMarkAsRead(notification.id, e)}
                              sx={{ 
                                color: safeLineColors.flameOrange,
                                '&:hover': {
                                  backgroundColor: alpha(safeLineColors.flameOrange, 0.1)
                                }
                              }}
                            >
                              <MarkReadIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton 
                            onClick={(e) => handleDeleteNotification(notification.id, e)}
                            sx={{ 
                              color: safeLineColors.mutedGray,
                              '&:hover': {
                                color: safeLineColors.red,
                                backgroundColor: alpha(safeLineColors.red, 0.1)
                              }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ 
                  p: 3, 
                  display: 'flex', 
                  justifyContent: 'center',
                  backgroundColor: safeLineColors.orangeTint,
                  borderTop: `1px solid ${safeLineColors.coolGray}`
                }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(e, page) => setCurrentPage(page)}
                    color="primary"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        '&.Mui-selected': {
                          backgroundColor: safeLineColors.flameOrange,
                          color: safeLineColors.pureWhite,
                          '&:hover': {
                            backgroundColor: safeLineColors.deepOrange
                          }
                        }
                      }
                    }}
                  />
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ 
              p: 6, 
              textAlign: 'center',
              backgroundColor: safeLineColors.orangeTint
            }}>
              <NotificationsIcon sx={{ 
                fontSize: 64, 
                color: safeLineColors.mutedGray,
                mb: 2
              }} />
              <Typography variant="h6" sx={{ 
                color: safeLineColors.mediumCharcoal,
                fontWeight: 'medium',
                mb: 1
              }}>
                No notifications found
              </Typography>
              <Typography variant="body2" sx={{ 
                color: safeLineColors.mutedGray,
                mb: 3
              }}>
                {searchQuery || typeFilter !== 'all' || statusFilter !== 'all' || showUnreadOnly
                  ? 'Try adjusting your filters to see more notifications.'
                  : 'You have no notifications at this time.'}
              </Typography>
              {(searchQuery || typeFilter !== 'all' || statusFilter !== 'all' || showUnreadOnly) && (
                <Button 
                  variant="outlined" 
                  onClick={clearFilters}
                  sx={{ 
                    borderColor: safeLineColors.flameOrange,
                    color: safeLineColors.flameOrange,
                    '&:hover': {
                      backgroundColor: alpha(safeLineColors.flameOrange, 0.1)
                    }
                  }}
                >
                  Clear All Filters
                </Button>
              )}
            </Box>
          )}
        </Card>
      </Container>
    </Box>
  );
};

export default NotificationsPage; 